import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import {
  CAMPAIGN_SEARCH_MAX_LENGTH,
  PLATFORM_LABELS,
  SOURCE_LINK_KIND_LABELS,
  campaignCategoryLabel,
  formatCents,
  isCampaignCategory,
  type CampaignCategory,
  type CampaignListResponse,
  type CreatorAccountSummary,
  type PublicCampaign,
} from '@nexus/shared';

import { useSession } from '../auth/SessionContext';
import { apiGet } from '../lib/api';
import {
  Alert,
  Badge,
  Card,
  EmptyState,
  Glyph,
  LoadErrorPage,
  LoadingPage,
  Meter,
  Money,
  Num,
  PageHeader,
  Pill,
  Pills,
  SearchField,
  Stat,
  StatGrid,
  formatNumber,
} from '../ui';

/**
 * Campaigns: find one.
 *
 * ## What changed, and why
 *
 * This page used to render every live campaign as a full-width card carrying its
 * rules, its terms text, an estimator and a Join button — about a screen and a
 * half each. That works at two campaigns and is unusable at twenty: a creator
 * looking for the music ones had to scroll past six others reading rate cards.
 * So the page is now a FINDER — a search box, a row of category pills, and a grid
 * of compact cards — and everything a card used to hold moved to
 * `/campaigns/:slug`, which is `CampaignDetail.tsx`.
 *
 * ## Three rules this page keeps from the old one
 *
 * INELIGIBLE CAMPAIGNS ARE SHOWN WITH THE REASON, NEVER HIDDEN. That is the
 * plan's wording and it survives the rewrite: on day one every creator is
 * ineligible for everything ("no connected account"), so a page that hid what you
 * cannot take would be an empty page for the entire pilot cohort. The card is
 * dashed, the pill says "Why not", and the first reason is on it.
 *
 * THE SEARCH AND THE FILTER ARE IN THE URL, so a reload keeps them, the back
 * button works, and a creator can send the operator the exact view they are
 * looking at. Typing debounces into the URL rather than pushing a history entry
 * per keystroke.
 *
 * THE QUERY RUNS IN SQL. `?q=` and `?category=` go to the API, which matches the
 * name with an escaped LIKE. A page that fetched everything and filtered the
 * array would be filtering whatever it happened to have.
 *
 * ## The strip at the top
 *
 * Every figure on it comes from the API, out of the SAME functions the Wallet and
 * "My submissions" read — `creatorTotals` for the money and `countSubmissions`
 * for the counts. Nothing here is derived from the campaign list, which is why
 * the wallet balance on this page and the wallet balance on /wallet cannot
 * disagree.
 */

/** What the URL says, defended against anything a person can type into it. */
function readQuery(params: URLSearchParams): { q: string; category: CampaignCategory | '' } {
  const category = params.get('category') ?? '';
  return {
    q: params.get('q') ?? '',
    /* An unknown category reads as "no filter" HERE and is a 400 at the API.
       Both are deliberate: the page must render rather than blank out, and the
       API must not quietly answer a different question than it was asked. */
    category: isCampaignCategory(category) ? category : '',
  };
}

export function Campaigns(): React.JSX.Element {
  const [params, setParams] = useSearchParams();
  const { q, category } = readQuery(params);

  const [data, setData] = useState<CampaignListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const { status, session } = useSession();

  /**
   * The box is local and the URL follows it after a pause.
   *
   * Writing every keystroke into the URL would put eight history entries behind
   * one word and fire eight requests. `replace` rather than push, for the same
   * reason: typing is not navigation. Same shape as "My submissions".
   */
  const [text, setText] = useState(q);
  const typed = useRef(false);
  useEffect(() => {
    /* The URL wins whenever it changed on its own — the back button, or a link.
       Only a keystroke starts the debounce. */
    if (!typed.current) setText(q);
  }, [q]);

  useEffect(() => {
    if (!typed.current) return;
    const timer = setTimeout(() => {
      typed.current = false;
      setParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          if (text) next.set('q', text);
          else next.delete('q');
          return next;
        },
        { replace: true },
      );
    }, 300);
    return () => clearTimeout(timer);
  }, [text, setParams]);

  function setCategory(value: CampaignCategory | ''): void {
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      if (value) next.set('category', value);
      else next.delete('category');
      return next;
    });
  }

  const load = useCallback(async () => {
    const search = new URLSearchParams();
    if (q) search.set('q', q);
    if (category) search.set('category', category);
    /* The account strip is opt-in at the API: it is ten extra reads, and the
        two other screens that call this endpoint only want the picker. */
    search.set('summary', '1');
    setError(null);
    try {
      return await apiGet<CampaignListResponse>(`/campaigns?${search.toString()}`);
    } catch {
      setError('Could not load campaigns.');
      return null;
    }
  }, [q, category]);

  useEffect(() => {
    /* Wait for the session answer: the eligibility verdict and the strip only
       come back for a signed-in creator, so asking first and again would render
       the page twice. */
    if (status === 'loading') return;
    let cancelled = false;
    void (async () => {
      const body = await load();
      if (!cancelled && body) setData(body);
    })();
    return () => {
      cancelled = true;
    };
  }, [status, load, attempt]);

  if (error && data === null) {
    return (
      <LoadErrorPage
        eyebrow="Creator"
        title="Campaigns"
        heading="Could not load campaigns."
        onRetry={() => {
          setError(null);
          setAttempt((a) => a + 1);
        }}
      >
        <span>
          The campaign list is served by the API and it did not answer. Nothing you have joined is
          affected; if it keeps happening, the operator will see it on the status board.
        </span>
      </LoadErrorPage>
    );
  }

  if (data === null) return <LoadingPage eyebrow="Creator" title="Campaigns" stats />;

  const filtered = Boolean(q || category);
  const joined = data.campaigns.filter((c) => c.participation?.status === 'JOINED').length;

  return (
    <>
      <PageHeader
        eyebrow="Creator"
        title="Campaigns"
        lead={
          session
            ? 'Search by name or pick a niche. Campaigns you do not qualify for are still here, with the reason on the card.'
            : 'Every live campaign. Sign in to see whether you qualify for one.'
        }
        actions={
          !session && (
            <Link className="btn btn-primary" to="/login">
              Sign in
            </Link>
          )
        }
      />

      {data.summary && <AccountStrip summary={data.summary} />}

      {/* A REFRESH THAT FAILED HAS TO SAY SO. The full-page error only appears
          when there is nothing to show at all, so a search that the API refused
          used to leave the PREVIOUS results on screen under the new search term
          — a grid that silently answers a different question than the box says.
          The rows stay (they are real, they are just old) and the sentence says
          which. */}
      {error && (
        <Alert tone="critical">
          {error} These are the results from before — the search you just typed did not run.
        </Alert>
      )}

      <Card tight label="Find a campaign">
        <div className="finder">
          <SearchField
            label="Search campaigns by name"
            visuallyHiddenLabel
            placeholder="Search campaigns by name"
            /* The API refuses more than this and the box should never be able to
               ask for it: a pasted paragraph would otherwise be a 400 the
               creator has to read the sentence above to understand. */
            maxLength={CAMPAIGN_SEARCH_MAX_LENGTH}
            value={text}
            onChange={(event) => {
              typed.current = true;
              setText(event.target.value);
            }}
          />
          {/* Only the categories with something live in them, with their counts.
              Fifteen pills over two campaigns would be thirteen ways to reach an
              empty page, and the API leaves the empty ones out. */}
          <Pills label="Filter by category">
            <Pill selected={category === ''} count={data.totalLive} onClick={() => setCategory('')}>
              All
            </Pill>
            {data.categories.map((row) => (
              <Pill
                key={row.category}
                selected={category === row.category}
                count={row.count}
                onClick={() => setCategory(row.category)}
              >
                {campaignCategoryLabel(row.category)}
              </Pill>
            ))}
          </Pills>
        </div>
      </Card>

      {data.campaigns.length === 0 ? (
        <Card>
          {filtered ? (
            /* "Nothing matched" and "nothing is live" are different sentences, and
               an empty array cannot tell them apart — which is what `totalLive`
               is for. */
            <EmptyState
              title={
                data.totalLive === 0
                  ? 'No campaigns are live right now.'
                  : `None of the ${formatNumber(data.totalLive)} live campaign${data.totalLive === 1 ? '' : 's'} match that.`
              }
              action={
                <button type="button" className="btn btn-ghost" onClick={() => setParams({})}>
                  Clear the search
                </button>
              }
            >
              {data.totalLive === 0
                ? 'A campaign appears here the moment a client funds one and the operator makes it live.'
                : 'Try a shorter word, or a different niche.'}
            </EmptyState>
          ) : (
            <EmptyState
              title="No campaigns are live right now."
              action={
                <Link className="btn btn-ghost" to="/submissions">
                  See your past submissions
                </Link>
              }
            >
              A campaign appears here the moment a client funds one and the operator makes it live.
              Nothing you need to do in the meantime.
            </EmptyState>
          )}
        </Card>
      ) : (
        <>
          <p className="muted" data-testid="result-count">
            {filtered
              ? `${formatNumber(data.campaigns.length)} of ${formatNumber(data.totalLive)} live campaigns`
              : `${formatNumber(data.campaigns.length)} live campaign${data.campaigns.length === 1 ? '' : 's'}`}
            {joined > 0 && `, ${formatNumber(joined)} you have joined`}.
          </p>
          <div className="campaign-grid">
            {data.campaigns.map((campaign) => (
              <CampaignCard key={campaign.id} campaign={campaign} />
            ))}
          </div>
        </>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* The strip: this creator's own account                               */
/* ------------------------------------------------------------------ */

/**
 * The top of the page: what is in the wallet, and how the work is going.
 *
 * The WALLET is one large figure and a link, because it is the number a creator
 * opens the app for and the operator asked for it to take them to /wallet. It is
 * a whole panel rather than an underlined number: a 44px block that names itself
 * is what a thumb can hit.
 *
 * The four counts beside it are deliberately NOT five equal tiles with the
 * wallet: a grid of five identical boxes says these are five equally important
 * things, and they are not.
 */
function AccountStrip({ summary }: { summary: CreatorAccountSummary }): React.JSX.Element {
  const { submissions } = summary;
  /**
   * ACCEPTED, which is approved plus paid — and the tile says ACCEPTED.
   *
   * `CREATOR_SUBMISSION_STATES` treats the two as a partition: a VERIFIED row
   * whose money has gone out says PAID and not APPROVED. So a tile showing only
   * the APPROVED count would drop every clip a creator has already been paid
   * for, and a tile showing the sum under the word "Approved" would be a figure
   * that does not match the Approved filter on "My submissions" — which is
   * exactly the disagreement this strip exists not to create. The sum, under a
   * word that means the sum, and a note that names both halves so either number
   * can be found on the other page.
   */
  const accepted = submissions.approved + submissions.paid;

  return (
    <Card variant="feature" tight label="Your account">
      <div className="account-strip">
        <Link className="wallet-tile" to="/wallet" data-testid="wallet-tile">
          <span className="wallet-label">Wallet</span>
          <span className="wallet-amount">{formatCents(summary.availableCents)}</span>
          <span className="wallet-note">Available to request →</span>
        </Link>

        <StatGrid dense>
          <Stat
            label="Earned all time"
            value={<Money cents={summary.lifetimeEarnedCents} />}
            note="Every earning that was not voided."
          />
          <Stat
            label="Submissions"
            value={<Num value={submissions.total} />}
            note="Clips you have submitted."
          />
          <Stat
            label="Pending review"
            value={<Num value={submissions.pending} />}
            note={submissions.pending === 0 ? 'Nothing waiting on us.' : 'Waiting on the operator.'}
          />
          <Stat
            label="Accepted"
            value={<Num value={accepted} />}
            note={
              submissions.paid > 0
                ? `${formatNumber(submissions.approved)} approved, ${formatNumber(submissions.paid)} already paid.`
                : 'Approved, and earning on their views.'
            }
          />
        </StatGrid>
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* One card                                                            */
/* ------------------------------------------------------------------ */

/**
 * A campaign, compact, as a link to its own page.
 *
 * THE WHOLE CARD IS THE LINK, so a thumb has a 100%-wide target rather than a
 * title to aim at, and there is nothing else interactive inside it — the Join
 * button is on the detail page, where the terms it agrees to are actually on
 * screen. A Join on a card that does not show the words being agreed to is the
 * thing `CampaignParticipation.documentHash` exists not to be.
 *
 * Seven facts and no more: brand, name, niche, rate, creators on it, time left,
 * how much budget is left — and the verdict, which is never hidden.
 */
function CampaignCard({ campaign }: { campaign: PublicCampaign }): React.JSX.Element {
  const eligibility = campaign.eligibility;
  const joined = campaign.participation?.status === 'JOINED';
  const removed = campaign.participation?.status === 'REMOVED';
  const applied = campaign.participation?.status === 'APPLIED';
  const declined = campaign.participation?.status === 'DECLINED';
  const ineligible = eligibility !== undefined && !eligibility.eligible;
  const applyFirst = campaign.joinMode === 'APPLY';

  const state = joined ? 'joined' : ineligible || removed ? 'ineligible' : 'open';

  return (
    <Link className="campaign-card" data-state={state} to={`/campaigns/${campaign.slug}`}>
      <div className="campaign-card-top">
        <span className="campaign-brand">{campaign.brand.displayName}</span>
        {/* NEVER HIDDEN, one of three words. A creator has to be able to tell
            from the grid whether a campaign is theirs to take. */}
        {removed ? (
          <Badge tone="critical">Removed</Badge>
        ) : joined ? (
          <Badge tone="positive">Joined</Badge>
        ) : applied ? (
          <Badge tone="waiting">Applied</Badge>
        ) : declined ? (
          <Badge tone="critical">Declined</Badge>
        ) : eligibility ? (
          <Badge tone={eligibility.eligible ? 'positive' : 'waiting'}>
            {eligibility.eligible ? 'Eligible' : 'Why not'}
          </Badge>
        ) : null}
      </div>

      <h3>{campaign.name}</h3>
      <div className="campaign-card-top">
        <span className="chip quiet">{campaignCategoryLabel(campaign.category)}</span>
        {/* A LOCK, so a creator can tell from the grid that this one has a
            door: pressing Join on it records an application, not a
            membership. */}
        {applyFirst && !joined && (
          <span className="chip lock" data-testid="apply-first">
            <Glyph name="lock" size={14} />
            Apply first
          </span>
        )}
      </div>

      <RateRow campaign={campaign} />

      <dl className="card-figures">
        <div>
          <dt>Creators</dt>
          <dd>
            <span className="with-glyph">
              <Glyph name="people" size={14} />
              {formatNumber(campaign.activity.creatorsJoined)}
            </span>
          </dd>
        </div>
        <div>
          <dt>Per clip</dt>
          <dd>
            {campaign.perSubmissionCapCents === null ? (
              <span className="unit">No cap</span>
            ) : (
              <>
                up to {formatCents(campaign.perSubmissionCapCents)}
              </>
            )}
          </dd>
        </div>
        <div>
          <dt>Time left</dt>
          <dd>{timeLeft(campaign)}</dd>
        </div>
      </dl>

      {/**
       * HOW MUCH IS LEFT, as a bar.
       *
       * "$2,340 left of $6,000" is a fact a creator has to do arithmetic on
       * before it answers the question they are asking, which is whether this is
       * nearly over and not worth editing for. The figures stay under it, because
       * a bar on its own is decoration.
       */}
      <Meter
        value={campaign.budgetRemainingCents}
        max={campaign.budgetCents}
        tone={budgetTone(campaign)}
        label={`${formatCents(campaign.budgetRemainingCents)} left of ${formatCents(campaign.budgetCents)}`}
      />
      <span className="budget-line">
        {formatCents(campaign.budgetRemainingCents)} left of{' '}
        {formatCents(campaign.budgetCents)}
      </span>

      {/* The first reason, so "Why not" is actionable from the grid. The rest,
          with the links that fix them, are on the detail page. */}
      {ineligible && eligibility.reasons[0] && (
        <span className="why-not" data-reason={eligibility.reasons[0].code}>
          {eligibility.reasons[0].message}
          {eligibility.reasons.length > 1 &&
            ` +${eligibility.reasons.length - 1} more`}
        </span>
      )}
      {removed && (
        <span className="why-not">
          {campaign.participation?.removedReason ?? 'You were removed from this campaign.'}
        </span>
      )}
    </Link>
  );
}

/**
 * The rate, per platform, on one line.
 *
 * "$1.50 TikTok · $1.00 Instagram · $0.80 YouTube" when the figures differ,
 * and a single "$0.25 / 1k" when they do not — a card that printed three
 * identical figures would be a card that made a creator check whether they
 * were identical.
 */
function RateRow({ campaign }: { campaign: PublicCampaign }): React.JSX.Element {
  const rates = campaign.platformRates;
  const distinct = new Set(rates.map((r) => r.ratePerThousandCents));
  if (rates.length === 0 || distinct.size <= 1) {
    const cents = rates[0]?.ratePerThousandCents ?? campaign.ratePerThousandCents;
    return (
      <p className="rate-row" data-testid="rate-row">
        <span>
          {formatCents(cents)} <span className="unit">/ 1k</span>
        </span>
      </p>
    );
  }
  return (
    <p className="rate-row" data-testid="rate-row">
      {rates.map((row, index) => (
        <span key={row.platform}>
          {index > 0 && (
            <span className="rate-sep" aria-hidden="true">
              ·
            </span>
          )}
          {formatCents(row.ratePerThousandCents)}
          <span className="rate-platform">{PLATFORM_LABELS[row.platform]}</span>
        </span>
      ))}
    </p>
  );
}

/* ------------------------------------------------------------------ */
/* Shared with the detail page                                         */
/* ------------------------------------------------------------------ */

/**
 * How long is left, in the units a person thinks in.
 *
 * The submission deadline wins over the end date when it is sooner, because it is
 * the one that stops a creator posting: a campaign that runs another month but
 * closes to new clips on Friday has three days left, not thirty.
 */
export function timeLeft(campaign: PublicCampaign): React.ReactNode {
  const ends = [campaign.submissionDeadline, campaign.endsAt]
    .filter((value): value is string => Boolean(value))
    .map((value) => Date.parse(value))
    .filter((value) => Number.isFinite(value))
    .sort((a, b) => a - b)[0];
  if (ends === undefined) return <span className="muted">No end date</span>;

  const ms = ends - Date.now();
  if (ms <= 0) return <span className="tone-critical">Closed</span>;
  const hours = Math.floor(ms / 3_600_000);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'}`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'}`;
}

/** Amber under a fifth of the budget, red under a twentieth. */
export function budgetTone(campaign: PublicCampaign): 'positive' | 'waiting' | 'critical' {
  if (campaign.budgetCents <= 0) return 'critical';
  const left = campaign.budgetRemainingCents / campaign.budgetCents;
  if (left <= 0.05) return 'critical';
  if (left <= 0.2) return 'waiting';
  return 'positive';
}

export function platformName(platform: string): string {
  return platform === 'TIKTOK' ? 'TikTok' : platform === 'INSTAGRAM' ? 'Instagram' : 'YouTube';
}

export function sourceKind(kind: string): string {
  return SOURCE_LINK_KIND_LABELS[kind as keyof typeof SOURCE_LINK_KIND_LABELS] ?? 'Link';
}
